import dayjs from 'dayjs';
import { Button, Form, Spin } from 'antd';
import utc from 'dayjs/plugin/utc';
import { Box, Button as MuiButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AutoIcon } from 'assets/icons/common';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { useControlData } from '../create/LeaseV9NewTrailModeCreate';
import LeaseV9NewTrailModeScheduleView from './LeaseV9NewTrailModeScheduleView';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import LeaseV9NewTrailModeDetailsForm from '../create/LeaseV9NewTrailModeDetailsForm';
import { utcConverter } from '../../lease-record-management-v9-new-version/utils/helperFunc';
import LeaseV9NewTrailModeHistory from './LeaseV9NewTrailModeHistory';
import LeaseV9NewGeneralLedgerView from './LeaseV9NewGeneralLedgerView';
import LeaseV9NewTrailModeBalanceView from './LeaseV9NewTrailModeBalanceView';
import EditIcon from 'assets/icons/edit';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import SectionCard from 'components/section/SectionCard';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';

dayjs.extend(utc);

const detailSectionCardSx = {
	padding: '16px',
	borderRadius: '10px',
	border: '1px solid #e6ebf2',
};

const LeaseV9NewTrailModeView = ({ screen = 'CREATE' }) => {
	const [form] = Form.useForm();
	const { state, pathname } = useLocation();
	const pinStorageKey = `lease-test-bed-v9-details:pinned:${pathname}`;

	const TrialBalanceRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();
	const [selectedTab, setSelectedTab] = useState(0);
	const [selectHistory, setSelectHistory] = useState(null);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');

	const { profileData } = useSelector((state) => state?.profile);

	const { lynFormProperties } = useControlData();
	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const statusData = useMemo(() => {
		return !(
			singleLeaseData?.activationStatus === 'W-I-P' &&
			singleLeaseData?.recordStatus === 'parked' &&
			profileData?.userId === singleLeaseData?.approverUserId[0]
		)
			? singleLeaseData?.data
			: singleLeaseData?.parkedData;
	}, [singleLeaseData]);

	useEffect(() => {
		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			leaseId: '',
			leaseTitle: '',
			leaseType: 'Finance Lease',
			lessorName: '',
			leaseTerm: '',
			leaseEndDate: '',
			creationDate: dayjs(),
			lastModifiedDate: dayjs(),
			escalationPercentage: '0',
			inputChangeMonth: '1',
			grossRentList: [
				{
					from: 1,
					to: '',
					amount: '',
					remarks: '',
				},
			],
			grossRentListSet: [[]],
			advanceAmountListSet: [[]],

			advanceAmountList: [
				{
					from: 1,
					to: '',
					amount: '',
					totalAmount: '',
				},
			],
			advanceAmount: '0',
			advanceDuration: '0',
			upfrontLeasePayment: '',
			initialDirectCost: '',
			securityDeposit: '',
			initialDiscountRate: '0',
			// rightOfUseAssetValue: '10000250 - RoU Front Offices',
			// glleaseLiability: '20001120 - Lease Liabilities',
			// costCenterInterestExpense: '902001 - Finance Common',
			// glInterestExpense: '40004250 - Interest for Lease',
			// leasePayableControlAccount: '20001100 - Lease Payable',
			// glAdvanceRent: '10002050 - Advance Rent',
			// depreciationRoUCostCenter: '902003 - Finance Common',
			// depreciationRoU: '20007555 - Depreciation RoU',
			// accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
			// glBankAccount: '10003001 - SCB Main',
			// leaseExpense: '40004260 - Interest for Lease',
			// leaseExpenseCostCenter: '902002 - Finance Common',
			// securityDepositHead: '10009008 - Security Deposite',
			// accountsPayableHead: '20003300 - Accounts Payable',
			identityId: '',
			cashIncentiveAmount: '',

			paymentModality: 'Start',
			paymentFrequency: 'Monthly',
			endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
			changes: [
				{
					inputChangeEffectiveDate: '',
					inputChangeAdvance: '',
					inputChangeAdvanceAmounts: [],
					inputChangeDescription: 'Initial Rent',
					inputChangeDiscountRate: 0,
					inputChangeEscalation: 0,
					inputChangeGrossAmount: 0,
					inputChangeGrossRents: [],
					inputChangeLeaseTerm: '',
					paymentFrequency: 'Monthly',
					paymentTime: 'Start',
				},
			],
			agreementFiles: [
				{
					id: '',
					name: '',
					file: '',
				},
			],
		}),
		[]
	);

	useEffect(() => {
		if (statusData) {
			const viewValues = {
				// General Lease Information section
				tabIndex: '1',
				modificationSequence: statusData?.modificationSequence,
				leaseId: statusData?.leaseId,
				leaseType: statusData?.leaseType,
				leaseTitle: statusData?.leaseTitle,
				lessorName: statusData?.lessorName,
				lessorID: statusData?.lessorID,
				paymentFrequency: statusData?.paymentFrequency,
				paymentModality: statusData?.paymentModality,
				creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
				lastModifiedDate: dayjs(statusData?.lastModifiedDate),

				// Financial Terms & Conditions section
				advanceAmount: statusData?.advanceAmount,
				advanceDuration: statusData?.advanceDuration,
				grossRent: statusData?.grossRent,
				leaseTerm: statusData?.leaseTerm,
				leaseStartDate: dayjs(statusData?.leaseStartDate),
				leaseEndDate: dayjs(statusData?.leaseEndDate),
				escalationPercentage: statusData?.escalationPercentage,
				initialDiscountRate: statusData?.initialDiscountRate,
				upfrontLeasePayment: statusData?.upfrontLeasePayment ?? 0,
				initialDirectCost: statusData?.initialDirectCost ?? 0,
				securityDeposit: statusData?.securityDeposit ?? 0,
				endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
				// Accounting Information section
				rightOfUseAssetValue: statusData?.rightOfUseAssetValue,
				glleaseLiability: statusData?.glleaseLiability ?? '',
				costCenterInterestExpense: statusData?.costCenterInterestExpense,
				glInterestExpense: statusData?.glInterestExpense,
				leaseExpenseCostCenter: statusData?.leaseExpenseCostCenter,
				leaseExpense: statusData?.leaseExpense,
				leasePayableControlAccount: statusData?.leasePayableControlAccount,
				glAdvanceRent: statusData?.glAdvanceRent,
				depreciationRoUCostCenter: statusData?.depreciationRoUCostCenter,
				depreciationROU: statusData?.depreciationRoU,
				accumDepreciationROU: statusData?.accumDepreciationRoU,
				glBankAccount: statusData?.glBankAccount,
				securityDepositHead: statusData?.securityDepositHead,
				accountsPayableHead: statusData?.accountsPayableHead,
				effectiveDateHistory: statusData?.effectiveDateHistory,
				cashIncentiveAmount: statusData?.cashIncentiveAmount || statusData?.cashIncentive,
				//others
				version: String(statusData?.version),
				leaseLiability: statusData?.leaseLiability,
				amortizationSchedule: statusData?.amortizationSchedule,
				leaseCategory: statusData?.leaseCategory,
				leaseName: statusData?.leaseName,
				contactDetails: statusData?.contactDetails,
				identityId: singleLeaseData?._id,

				renewalOptions: statusData?.renewalOptions,
				terminationOption: statusData?.terminationOption,
				month: statusData?.month,
				refundability: statusData?.refundability,
				agreementFiles: statusData?.agreementFiles,

				responsibility: statusData?.responsibility,
				basisOfRate: statusData?.basisOfRate,
				nonLeaseComponents: statusData?.nonLeaseComponents,
				separateServiceCharges: statusData?.separateServiceCharges,
				modificationDetails: statusData?.modificationDetails,
				discountRateChanges: statusData?.discountRateChanges,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				grossRentList: statusData?.grossRentList,
				grossRentListSet: statusData?.grossRentListSet,
				advanceAmountListSet: statusData?.advanceAmountListSet,
				advanceAmountList: statusData?.advanceAmountList,
				//glValues
				gl_rouAssetInitialRecognition: statusData?.gl_rouAssetInitialRecognitionValue,
				gl_rouAssetModification: statusData?.gl_rouAssetModificationValue,
				gl_rouAssetPrepayments: statusData?.gl_rouAssetPrepaymentsValue,
				gl_rouAssetUpfrontPayment: statusData?.gl_rouAssetUpfrontPaymentValue,

				gl_rouAssetIndirectCosts: statusData?.gl_rouAssetIndirectCostsValue,
				gl_rouCashIncentive: statusData?.gl_rouCashIncentiveValue,
				gl_rouAssetKnockOff: statusData?.gl_rouAssetKnockOffValue,
				gl_securityDeposit: statusData?.gl_securityDepositValue,
				gl_leaseLiabilityInitialRecognition: statusData?.gl_leaseLiabilityInitialRecognitionValue,
				gl_leaseLiabilityModification: statusData?.gl_leaseLiabilityModificationValue,
				gl_leaseLiabilityPayment: statusData?.gl_leaseLiabilityPaymentValue,
				gl_leaseLiabilityInterest: statusData?.gl_leaseLiabilityInterestValue,
				gl_interestExpense: statusData?.gl_interestExpenseValue,
				gl_depreciationRou: statusData?.gl_depreciationRouValue,
				gl_accumDepreciationRouDepreciation: statusData?.gl_accumDepreciationRouDepreciationValue,
				gl_accumDepreciationRouKnockOff: statusData?.gl_accumDepreciationRouKnockOffValue,
				gl_apControlLedgerPrepayments: statusData?.gl_apControlLedgerPrepaymentsValue,
				gl_apControlLedgerUpfrontPayment: statusData?.gl_apControlLedgerUpfrontPaymentValue,
				gl_apControlLedgerIndirectCosts: statusData?.gl_apControlLedgerIndirectCostsValue,
				gl_apControlLedgerSecurityDeposit: statusData?.gl_apControlLedgerSecurityDepositValue,
				gl_apControlLedgerPayment: statusData?.gl_apControlLedgerPaymentValue,
				gl_arCashIncentive: statusData?.gl_arCashIncentiveValue,

				//changes
				changes: statusData?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
					};
				}),
			};

			form.setFieldsValue(viewValues);

			requestAnimationFrame(() => {
				setDataFullyLoaded(true);
			});
		}
	}, [singleLeaseData]);

	const selectTabHandler = (_, newValue) => {
		setSelectedTab(newValue);

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 3 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (TrialBalanceRef.current?.loadData) {
				TrialBalanceRef.current.loadData();
			}
		}
	};

	useEffect(() => {
		setIsHeaderPinned(sessionStorage.getItem(pinStorageKey) !== '0');
	}, [pinStorageKey]);

	const handleAutoPinToggle = () => {
		const nextState = !isHeaderPinned;
		setIsHeaderPinned(nextState);
		sessionStorage.setItem(pinStorageKey, nextState ? '1' : '0');
	};

	useEffect(() => {
		if (singleLeaseData && singleLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = singleLeaseData.activationStatus;
		}
	}, [singleLeaseData]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				<LeaseV9NewTrailModeDetailsForm
					viewData={singleLeaseData}
					screen="VIEW"
					showFooterActions={false}
					vendorList={lynFormProperties?.vendorList}
				/>
			),
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<SectionCard sx={detailSectionCardSx}>
					<LeaseV9NewTrailModeScheduleView
						// screen={screen}
						// ref={leaseScheduleRef}
						viewData={singleLeaseData}
						dataFullyLoaded={dataFullyLoaded}
					/>
				</SectionCard>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: (
				<SectionCard sx={detailSectionCardSx}>
					<LeaseV9NewTrailModeHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</SectionCard>
			),
		},
		{
			key: '4',
			label: 'General Ledgers',
			children: (
				<SectionCard sx={detailSectionCardSx}>
					<LeaseV9NewGeneralLedgerView ref={leaseDetailsShowAllRef} />
				</SectionCard>
			),
		},
		{
			key: '5',
			label: 'Trial Balance',
			children: (
				// <>
				// 	<CardAccordion
				// 		title=""
				// 		customHeaderSX={{
				// 			backgroundColor: '#e1f0ff !important',
				// 			p: '10px',
				// 		}}
				// 		typographySX={{ fontSize: '16px !important' }}
				// 		darkTitle={true}
				// 		sx={{ mb: 2 }}
				// 	>
				// 		<TrialBalance ref={TrialBalanceRef} />
				// 	</CardAccordion>
				// </>

				<SectionCard sx={detailSectionCardSx}>
					<LeaseV9NewTrailModeBalanceView />
				</SectionCard>
			),
		},
	];

	return (
		<Box>
			<Spin spinning={isViewLoading} style={{ maxHeight: '100%' }}>
				<Box
					sx={{
						mb: 2,
						backgroundColor: isHeaderPinned ? 'background.main' : 'transparent',
						top: isHeaderPinned ? '60px' : 'auto',
						zIndex: isHeaderPinned ? 20 : 'auto',
						position: isHeaderPinned ? 'sticky' : 'static',
						px: { xs: 2, md: 2.5 },
						pt: { xs: 1.75, md: 2 },
						bgcolor: '#fff',
						border: '1px solid #e6ebf2',
						borderRadius: '10px',
						boxShadow: 'none',
					}}
				>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						alignItems={{ xs: 'flex-start', md: 'center' }}
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 1.25 }}
					>
						<Box>
							<Typography sx={{ color: '#1f2937', fontSize: '18px', fontWeight: 600, lineHeight: 1.2 }}>
								View {state?.processName}
							</Typography>
						</Box>

						<TrailModeHeaderActions
							state={state}
							viewData={singleLeaseData}
							isHeaderPinned={isHeaderPinned}
							onAutoPinToggle={handleAutoPinToggle}
							showActionButtons={selectedTab === 0}
						/>
					</Stack>

					<Box
						sx={{
							px: { xs: 0.25, md: 0.5 },
							borderTop: '1px solid #eef2f7',
						}}
					>
						<Tabs
							value={selectedTab}
							onChange={selectTabHandler}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							sx={{
								minHeight: '36px',
								'& .MuiTabs-indicator': {
									height: '2px',
									backgroundColor: '#2563eb',
									borderRadius: '999px',
								},
								'& .MuiTab-root': {
									minHeight: '36px',
									px: { xs: 1.25, md: 1.5 },
									mr: { xs: 0.5, md: 1 },
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: 500,
									color: '#667085',
								},
								'& .Mui-selected': {
									color: '#2563eb !important',
									fontWeight: 600,
								},
							}}
						>
							{tabItem?.map((item) => (
								<Tab key={item.key} label={item.label} />
							))}
						</Tabs>
					</Box>
				</Box>

				<Form form={form} layout="vertical" autoComplete="off" requiredMark={false} initialValues={initialFormValues}>
					<AntdValuePreview form={form} show={false} />

					<Box>{tabItem?.[selectedTab]?.children}</Box>
				</Form>

				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						selectItem={selectHistory}
						setSelectHistory={setSelectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default LeaseV9NewTrailModeView;

const TrailModeHeaderActions = ({ state, viewData, isHeaderPinned, onAutoPinToggle, showActionButtons = true }) => {
	const navigate = useNavigate();
	const [showGoAndSetSubmitModal, setShowGoAndSetSubmitModal] = useState(false);
	const [goAndSetAction] = useSendControlDataMutation();

	const handleGoAndSetButton = async () => {
		const payload = {
			data: viewData?._id,
			processCode: state?.processCode,
			scriptName: 'lease-controller-go-and-set',
		};

		try {
			await goAndSetAction(payload);
			navigate(-1, { state });
		} catch (error) {
			console.error('Error in Go & Set action:', error);
		}
	};

	return (
		<>
			<Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
				<Tooltip title={isHeaderPinned ? 'Pinned' : 'Unpin'}>
					<MuiButton
						onClick={onAutoPinToggle}
						aria-label={isHeaderPinned ? 'Unpin header' : 'Pin header'}
						sx={{
							minWidth: '36px',
							width: '36px',
							height: '36px',
							borderRadius: '10px',
							color: isHeaderPinned ? '#1D4ED8' : '#374151',
							border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#E5E7EB'}`,
							backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
						}}
					>
						<AutoIcon />
					</MuiButton>
				</Tooltip>

				{showActionButtons && (
					<>
						<Button
							onClick={() => {
								if (navigate && state) {
									navigate('/static/lease-test-bed-v9/edit', { state });
								}
							}}
							style={{
								height: '34px',
								display: 'flex',
								alignItems: 'center',
								paddingInline: '16px',
								borderRadius: '8px',
								fontWeight: 500,
								color: '#344054',
								borderColor: '#d0d5dd',
								backgroundColor: '#ffffff',
								boxShadow: 'none',
							}}
							icon={<EditIcon width="15px" height="15px" color="#344054" />}
						>
							Modify
						</Button>

						<Button
							type="primary"
							onClick={() => setShowGoAndSetSubmitModal(true)}
							style={{
								height: '34px',
								paddingInline: '16px',
								borderRadius: '8px',
								fontWeight: 600,
								backgroundColor: '#2563eb',
								boxShadow: 'none',
							}}
						>
							Go & Set
						</Button>
					</>
				)}
			</Stack>

			{showGoAndSetSubmitModal && (
				<ComptrollerConfirmModal
					showModal={showGoAndSetSubmitModal}
					handleSubmit={handleGoAndSetButton}
					setShowModal={setShowGoAndSetSubmitModal}
					modalData={{
						head: state?.processName ?? '',
						label: 'Do you want to proceed?',
					}}
				/>
			)}
		</>
	);
};
